import { bind, Component, h, tag } from "omi";
import type { Editor } from "@tiptap/core";
import hlStyles from "highlight.js/styles/atom-one-light.min.css?inline";
import debounce from "lodash-es/debounce";
import { gzip } from "pako";
import tippyAnimations from "tippy.js/animations/scale-subtle.css?inline";

import { preprocessContent } from "@/editor/utils/preprocessContent";
import { EditorEvents } from "@/editor/events";
import { createEditorInstance, removeEditorInstance } from "@/editor/useEditor";
import globalStyles from "@/globals/styles.css?inline";
import { ErrorService } from "@/services/errorService";
import type { IntersectionService } from "@/services/intersectionService";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";
import { getImpersonationHeaders, initApi } from "@/utils";

import bookmarkStyles from "./extensions/bookmark/styles.css?inline";
import uuidStyles from "./extensions/uuid/styles.css?inline";
import { EditorWorkerMessageType, type EditorSaveWorkerMessage } from "./worker/events";
import { getEditorWorker, terminateEditorWorker } from "./worker/workerManager";
import { createExtensions } from "./extensions";
import styles from "./styles.css?inline";

type Props = {
  content: string;
  instanceId: string;
  saveAgainstTrack?: boolean;
  withScrollObservation?: boolean;
  withNodeHover?: boolean;
};

@tag("nw-editor")
export class NWEditor extends Component<Props> {
  static css = [
    tailwind,
    globalStyles,
    tippyAnimations,
    hlStyles,
    uuidStyles,
    bookmarkStyles,
    styles,
    `
    :host { width: 100%; height: 100%; }
    `,
  ];

  private editorInstance: Editor | undefined;
  private debouncedSave: (() => void) | undefined;
  private intersectionService: IntersectionService | undefined;

  installed() {
    if (!Store.features.hasFeature("projects.editor")) {
      return;
    }

    this.editorInstance = this.getEditorInstance();
    this.attachListeners();
    this.waitForEditorIdle();
  }

  uninstall() {
    if (this.editorInstance) {
      removeEditorInstance(this.props.instanceId);
      this.editorInstance = undefined;
    }

    this.removeListeners();
    this.intersectionService?.disconnectAll();
    this.intersectionService = undefined;
  }

  receiveProps(props: Omi.OmiProps<Props> | Props, oldProps: Omi.OmiProps<Props> | Props) {
    if (oldProps.content != props.content && this.editorInstance) {
      this.updateEditorContent(props.content);
      return false;
    }
  }

  @bind
  private getEditorInstance() {
    const editorRootElement = this.shadowRoot?.querySelector(".editor-root");

    if (editorRootElement == null) {
      return undefined;
    }

    const extensions = createExtensions(this.props.instanceId, {
      withNodeHover: this.props.withNodeHover ?? false,
      hasBookmarksFeature: Store.features.hasFeature("projects.bookmarks"),
    });
    return createEditorInstance(editorRootElement, extensions, this.props.content, this.props.instanceId);
  }

  private attachListeners() {
    addEventListener(EditorEvents.CLEAR_CHANGES, this.handleResetChanges);
    addEventListener(EditorEvents.TOGGLE_EDITING, this.handleToggleEditing);

    if (this.props.withScrollObservation) {
      document.addEventListener("scroll_to_step", this.scrollToStep as EventListener);
    }
  }

  private removeListeners() {
    removeEventListener(EditorEvents.CLEAR_CHANGES, this.handleResetChanges);
    removeEventListener(EditorEvents.TOGGLE_EDITING, this.handleToggleEditing);
    if (this.props.withScrollObservation) {
      removeEventListener("scroll_to_step", this.scrollToStep as EventListener);
    }
  }

  private updateEditorContent(content: string) {
    if (this.editorInstance) {
      const preprocessedContent = preprocessContent(content);
      this.editorInstance.commands.clearContent();
      this.editorInstance.commands.setContent(preprocessedContent);
      this.createIntersectionObserver();
    }
  }

  @bind
  private scrollToStep(event: CustomEvent): void {
    event.preventDefault();
    // Support both itemId (from TOC) and stepId (from nw-content-link)
    const id = event.detail?.itemId || event.detail?.stepId;

    if (!id) return;

    const targetItem =
      this.editorInstance?.view.dom.querySelector(`nw-section[stepid="${id}"]`) ||
      this.editorInstance?.view.dom.querySelector(`[node-id="${id}"]`) ||
      this.editorInstance?.view.dom.querySelector(`[id="${id}"]`);

    targetItem?.scrollIntoView({ behavior: "smooth" });
  }

  private waitForEditorIdle() {
    if (!this.editorInstance) return;
    const func = () => {
      if (this.editorInstance) {
        this.setupDebouncedSave();
        this.createIntersectionObserver();
      }
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(func);
    } else {
      // Fallback to setTimeout if requestIdleCallback is not available
      globalThis.setTimeout(func, 1);
    }
  }

  @bind
  createIntersectionObserver() {
    const steps = this.shadowRoot?.querySelectorAll("nw-section[stepid], nw-step");
    const boxes = this.shadowRoot?.querySelectorAll("nw-validation-box");

    if (steps || boxes) {
      import("@/services/intersectionService").then(({ IntersectionService }) => {
        this.intersectionService = new IntersectionService();
        steps &&
          this.intersectionService.startObservingIntersections(steps, {
            threshold: 0,
            rootMargin: "0px 0px -20% 0px",
          });
        boxes && this.intersectionService.startObservingIntersections(boxes, { threshold: 0 });
      });
    }
  }

  private setupDebouncedSave() {
    this.debouncedSave = debounce(() => {
      this.handleSaveDocument();
    }, 1000);

    this.editorInstance?.on("update", () => {
      this.debouncedSave?.();
    });
  }

  @bind
  private async handleResetChanges() {
    const api = initApi();
    const filename = this.getFilename();
    const projectId = Store.project.id.value;

    if (filename == null || projectId == null) {
      return;
    }

    await api.deleteProjectFile({ id: projectId, filename });
    const content = await api.getProjectFile({ id: projectId, filename });
    this.updateEditorContent(content);
  }

  @bind
  private async handleToggleEditing() {
    this.editorInstance?.setEditable(!this.editorInstance.isEditable);
  }

  @bind
  private handleSaveDocument() {
    try {
      // Check admin debug flag (for debugging purposes)
      if (!Store.editor.adminDebugSavingEnabled.value) {
        return;
      }

      // Check if user can save current project (Pro project access control)
      if (!Store.editor.canSaveCurrentProject()) {
        return;
      }

      const res = this.editorInstance?.getHTML();

      if (res == null) {
        ErrorService.report(new Error("Unable to retrieve content to save"));
        return;
      }

      this.saveContent(res);
    } catch (error) {
      ErrorService.report(error);
    }
  }

  @bind
  private saveContent(content: EditorSaveWorkerMessage["payload"]["body"]) {
    const filename = this.getFilename();

    if (filename == null) {
      return;
    }

    const message: EditorSaveWorkerMessage = {
      type: EditorWorkerMessageType.SAVE,
      basePath: window.location.origin,
      payload: {
        id: Store.project.id.value!,
        body: content,
        filename,
      },
      compress: true,
      impersonationHeaders: getImpersonationHeaders(),
    };

    const worker = getEditorWorker();

    if (worker) {
      this.saveContentViaWorker(worker, message);
    } else {
      this.saveContentDirectly(message);
    }
  }

  @bind
  private saveContentViaWorker(worker: Worker, message: EditorSaveWorkerMessage) {
    worker.postMessage(message);

    worker.onerror = (event: ErrorEvent) => {
      ErrorService.report(new Error(event.message));
      this.saveContentDirectly(message);
      terminateEditorWorker();
    };
  }

  private async saveContentDirectly(message: EditorSaveWorkerMessage) {
    const api = initApi();

    try {
      const body = message.payload;
      await api.updateProjectFile({ ...message.payload, body: new Blob([gzip(body.body)]) });
    } catch (error) {
      ErrorService.report(error);
    }
  }

  private getFilename(): string | undefined {
    if (this.props.saveAgainstTrack) {
      const track = Store.project.getSelectedTrackId();
      if (track == null) {
        ErrorService.report(new Error("No track selected to save against"));
        return undefined;
      }
      return track;
    }

    return this.props.instanceId;
  }

  render() {
    if (!Store.features.hasFeature("projects.editor")) {
      return;
    }

    return (
      <>
        <div class="editor-root"></div>
      </>
    );
  }
}
